/**
 * Regexes for URLs that should receive W3C trace context (traceparent) on fetch/XHR.
 *
 * Shared by both UI trees (src/app and src/dashboard) via core/.
 *
 * Deployed static app hosts are excluded: their edge CORS policy does not allow the
 * combined header set injected by browser telemetry (traceparent, Datadog, Authorization),
 * which breaks OPTIONS preflight and blocks the deployed-app bootstrap fetch.
 *   - Cloud SaaS: `*.deployed-apps.*.superblocks.com` (APPS-3085)
 *   - Cloud-prem:  `*.deployed.*.superblocks.com`     (AGENT-2929)
 */
export const PROPAGATE_TRACE_HEADER_URLS: RegExp[] = [
  new RegExp(
    "^https?://(?!login)(?![^/]*\\.deployed-apps\\.)(?![^/]*\\.deployed\\.).*superblocks\\.com",
  ),
  new RegExp(
    "^https?://(?!login)(?![^/]*\\.deployed-apps\\.)(?![^/]*\\.deployed\\.).*superblocks\\.dev",
  ),
  new RegExp("^https?://localhost:[0-9]+"),
  new RegExp("^https?://127.0.0.1:[0-9]+"),
];

/**
 * Build the propagate-allowlist, excluding the DP routing-config origin.
 *
 * routing.json is served from a CloudFront distribution
 * (SUPERBLOCKS_UI_DP_CONFIG_URL). Real deployments use raw
 * `*.cloudfront.net` URLs today (which never match the allowlist), but a
 * deployment that CNAMEs the distribution onto `*.superblocks.com` would
 * match — and CloudFront would then reject the CORS preflight carrying
 * traceparent, silently breaking DP gateway discovery. A trace header on a
 * static config fetch buys nothing, so the origin is excluded outright;
 * the request still produces a span.
 */
export function buildPropagateTraceUrls(dpConfigUrl?: string): RegExp[] {
  if (!dpConfigUrl) {
    return PROPAGATE_TRACE_HEADER_URLS;
  }

  let origin: string;
  try {
    origin = new URL(dpConfigUrl).origin;
  } catch {
    return PROPAGATE_TRACE_HEADER_URLS;
  }

  const escaped = origin.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  // Prepend a negative lookahead so the DP-config origin never receives
  // trace headers, regardless of which base pattern it would otherwise match.
  return PROPAGATE_TRACE_HEADER_URLS.map(
    (pattern) =>
      new RegExp(
        `^(?!${escaped}([/?#]|$))(?:${pattern.source})`,
        pattern.flags,
      ),
  );
}
